import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiGlobe } from 'react-icons/fi';
import { FaChevronDown, FaHeart, FaYoutube, FaDiscord, FaCrown, FaStar } from 'react-icons/fa';
import { FiUsers } from 'react-icons/fi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import bannerGif from '../../../../assets/images/banner.gif';
import logo from '../../../../assets/images/logo.png';

import { getServers } from '../../../../store/servers/serversSlice';
import { getContentCreators } from '../../../../store/contentCreators/contentCreatorsSlice';

import {
  HeroBanner,
  SectionHeader,
  ServersGrid,
  ServerCard,
  CreatorsSection,
  CreatorsRow,
  CreatorCard,
} from './HomeStyle';
import { backendUrl } from "../../../../utilities/axios";
import { SkeletonCard, SkeletonCreatorCard, NoData } from '../../../shared/WebsiteFeedback';

const Home = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { data: serversData, isLoading: serversLoading } = useSelector(state => state.servers);
  const { data: creatorsData, isLoading: creatorsLoading } = useSelector(state => state.contentCreators);
  const [headerServers, setHeaderServers] = useState([]);

  useEffect(() => {
    dispatch(getServers({ page: 1, size: 8, query: '', filter: JSON.stringify({ is_active: true, showInHome: true }), sort: 'order -created_at' }));
    dispatch(getContentCreators({ page: 1, size: 7, query: '', filter: JSON.stringify({ is_active: true, showInHome: true }) }));

    // Fetch the servers flagged to appear in the header (can be more than one)
    axios.get(`/api/get_servers?page=1&size=8&filter=${JSON.stringify({ is_active: true, showInHeader: true })}&sort=order -created_at`)
      .then(({ data }) => setHeaderServers(data?.data ?? []))
      .catch(() => setHeaderServers([]));
  }, [dispatch]);

  const servers = serversData?.data || [];
  const creators = creatorsData?.data || [];

  const getServerImage = (server) =>
    server?.image ? `${backendUrl}/servers/${server.image}` : bannerGif;

  const getCreatorAvatar = (creator) =>
    creator?.avatar ? `${backendUrl}/creators/${creator.avatar}` : logo;

  // Display the correct language variant, falling back to `name` / `description`
  const serverName = (s) =>
    i18n.language === 'ar'
      ? (s?.name_ar || s?.name || '')
      : (s?.name_en || s?.name || '');

  const serverDesc = (s) =>
    i18n.language === 'ar'
      ? (s?.description_ar || s?.description || '')
      : (s?.description_en || s?.description || '');

  const fameBadge = (server) => {
    if (server.fame === 'famous') return { label: t('website.home.fame_famous'), cls: 'famous' };
    if (server.fame === 'known') return { label: t('website.home.fame_known'), cls: 'known' };
    if (server.fame === 'unknown') return { label: t('website.home.fame_unknown'), cls: 'unknown' };
    return null;
  };

  // Hero slider uses the servers flagged showInHeader; falls back to the first active server
  const heroServers = headerServers.length > 0 ? headerServers : (servers[0] ? [servers[0]] : []);
  const isMultiHero = heroServers.length > 1;

  return (
    <WebsiteLayout>
      {/* ── HERO ── */}
      {heroServers.length > 0 && (
        <HeroBanner>
          <Swiper
            key={heroServers.map(s => s._id).join('-')}
            modules={[Autoplay, Navigation, Pagination]}
            dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            loop={isMultiHero}
            autoplay={isMultiHero ? { delay: 6000, disableOnInteraction: false } : false}
            navigation={isMultiHero}
            pagination={isMultiHero ? { clickable: true } : false}
            className="hero-swiper"
          >
            {heroServers.map(server => (
              <SwiperSlide key={server._id}>
                <img src={getServerImage(server)} alt="hero" className="hero-img" />
                <div className="hero-overlay" />
                <div className="hero-content">
                  <span className="status-badge">{t('website.home.available')}</span>
                  <div className="hero-title">{serverName(server)}</div>
                  {/* <p className="hero-desc">{serverDesc(server)}</p> */}
                  {server.website_url && (
                    <a
                      href={server.website_url}
                      target="_blank"
                      rel="noreferrer"
                      className="hero-btn"
                      onClick={e => e.stopPropagation()}
                    >
                      <FiGlobe size={13} />
                      {t('website.home.website_btn')}
                    </a>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </HeroBanner>
      )}

      {/* ── SERVERS GRID ── */}
      <SectionHeader>
        <div className="title">{t('website.home.servers_title')}</div>
        {/* <button className="filter-btn">
          {t('website.home.top_rated_filter')}
          <FaChevronDown />
        </button> */}
      </SectionHeader>

      {serversLoading ? (
        <ServersGrid>
          {Array.from({ length: 8 }, (_, i) => <SkeletonCard key={i} />)}
        </ServersGrid>
      ) : servers.length === 0 ? (
        <NoData />
      ) : (
        <ServersGrid>
          {servers.map((server, idx) => {
            const fame = fameBadge(server);
            return (
              <ServerCard key={server._id} as={Link} to={`/server/${server._id}`}>
                <div className="card-img-wrap">
                  <img src={getServerImage(server)} alt={serverName(server)} />
                  <span className="rank-badge">#{idx + 1}</span>
                </div>
                <div className="card-body">
                  <div className="card-top-row">
                    <div className="card-right-col">
                      <div className="card-name">{serverName(server)}</div>
                      <div className="card-stats">{t('website.home.wars_farms', { wars: server.wars, farms: server.farms })}</div>
                      <div className="card-level">Lv. {server.level}</div>
                      {fame && (
                        <div className="card-owner">
                          <span className="owner-label">صاحب السيرفر:</span>
                          <span className={`fame-badge ${fame.cls}`}>
                            {/* {fame.icon} */}
                            {fame.label}
                          </span>
                        </div>
                      )}
                      <span className="status-badge">{t('website.home.available')}</span>
                    </div>
                    <div className="card-left-col">
                      <div className="card-likes">
                        <FaHeart />
                        {(server.votes || 0).toLocaleString()}
                      </div>
                      <span className="platform-icon"><FaDiscord /></span>
                    </div>
                  </div>
                  {server.website_url && (
                    <a href={server.website_url} target="_blank" rel="noreferrer" className="website-btn"
                      onClick={e => e.stopPropagation()}>
                      <FiGlobe size={12} />
                      {t('website.home.website_btn')}
                    </a>
                  )}
                </div>
              </ServerCard>
            );
          })}
        </ServersGrid>
      )}

      {/* ── CREATORS ── */}
      <CreatorsSection>
        <SectionHeader>
          <div className="title">
            <FiUsers />
            {t('website.home.creators_title')}
          </div>
        </SectionHeader>
        {creatorsLoading ? (
          <CreatorsRow>
            {Array.from({ length: 7 }, (_, i) => <SkeletonCreatorCard key={i} />)}
          </CreatorsRow>
        ) : creators.length === 0 ? (
          <NoData />
        ) : (
          <CreatorsRow>
            {creators.map(creator => (
              <CreatorCard key={creator._id}>
                <div className="avatar-wrap">
                  <img src={getCreatorAvatar(creator)} alt={creator.name} />
                </div>
                <div className="creator-name">{creator.name}</div>
                <div className="creator-platform"><FaYoutube /></div>
              </CreatorCard>
            ))}
          </CreatorsRow>
        )}
      </CreatorsSection>
    </WebsiteLayout>
  );
};

export default Home;
